/* eslint-disable no-use-before-define */
import useUiPlanningStore from '@/stores/ui/planning';
import { openDependenciesConflictError } from './dependencyDialogs';
import { getAnchorTime, getDependencyAnchors } from './dependencyHelpers';

function setAnchorTime(el, anchor, anchorTime, duration, visibleTimeline) {
  if (anchor == 'start') {
    el.setStartTime(anchorTime);
    el.setEndTime(el.getStartTime().addWithWorkdays(duration, 'minutes', visibleTimeline.workdays));
    return;
  }
  el.setEndTime(anchorTime);
  el.setStartTime(el.getEndTime().addWithWorkdays(-duration, 'minutes', visibleTimeline.workdays));
}

function getMaxAnchorTime(el, anchor, duration, visibleTimeline) {
  if (anchor == 'start') {
    if (el.maxStart) return el.maxStart;
    if (! el.maxEnd) return null;
    return moment(el.maxEnd).addWithWorkdays(-duration, 'minutes', visibleTimeline.workdays);
  }
  if (el.maxEnd) return el.maxEnd;
  if (! el.maxStart) return null;
  return moment(el.maxStart).addWithWorkdays(duration, 'minutes', visibleTimeline.workdays);
}

function getMinAnchorTime(el, anchor, duration, visibleTimeline) {
  if (anchor == 'start') {
    if (el.minStart) return el.minStart;
    if (! el.minEnd) return null;
    return moment(el.minEnd).addWithWorkdays(-duration, 'minutes', visibleTimeline.workdays);
  }
  if (el.minEnd) return el.minEnd;
  if (! el.minStart) return null;
  return moment(el.minStart).addWithWorkdays(duration, 'minutes', visibleTimeline.workdays);
}

function clampStartTime(el, desiredStart, duration, visibleTimeline) {
  // helper for optimizeDependencies : clamp start time between the min and max allowed start times according to dependencies constraints.
  const minStart = getMinAnchorTime(el, 'start', duration, visibleTimeline);
  const maxStart = getMaxAnchorTime(el, 'start', duration, visibleTimeline);
  let clampedStart = desiredStart;

  if (minStart && clampedStart.isBefore(minStart)) clampedStart = moment(minStart);
  if (maxStart && clampedStart.isAfter(maxStart)) clampedStart = moment(maxStart);

  if (minStart && maxStart && minStart.isAfter(maxStart)) {
    const distanceToMin = Math.abs(desiredStart.diff(minStart));
    const distanceToMax = Math.abs(desiredStart.diff(maxStart));
    clampedStart = distanceToMin <= distanceToMax ? moment(minStart) : moment(maxStart);
  }

  return clampedStart;
}

function calculate(elements, visibleTimeline, reverse = false, optimizeDependencies = false) {
  // 1) Cleanup: remove one-way dependencies (A -> B without matching B -> A)
  const dependenciesByElId = elements.reduce((acc, el) => {
    const elDependencies = el.getDependencies();
    if (elDependencies && elDependencies.length) acc.set(el.id, { el, targetIds: new Set(elDependencies.map(item => item.target_id)) });
    return acc;
  }, new Map());
  dependenciesByElId.forEach(({ el, targetIds }, elId) => {
    targetIds.forEach((targetId) => {
      if (! dependenciesByElId.get(targetId)?.targetIds.has(elId)) {
        const elDependencies = el.getDependencies().slice(); // dependencies is mutable but items are not
        const index = elDependencies.findIndex(item => item.target_id == targetId);
        if (index > -1) {
          elDependencies.splice(index, 1);
          el.setDependencies(elDependencies);
        }
      }
    });
  });

  // 2) Graph construction: create a local copy of related elements and index dependencies for fast lookups.
  const relatedElsById = elements.reduce((acc, el) => {
    if (! el.getDependencies()?.length || ! el.hasDates()) return acc;
    acc.set(el.id, angular.copy(el));
    return acc;
  }, new Map());
  const dependencies = {};
  relatedElsById.forEach((el) => {
    el.predecessors = [];
    el.successors = [];
    el.getDependencies().forEach((dependency) => {
      const targetEl = relatedElsById.get(dependency.target_id);
      if (targetEl && dependency.successor) el.successors.push(targetEl);
      if (targetEl && ! dependency.successor) el.predecessors.push(targetEl);
      if (! dependencies[el.id]) dependencies[el.id] = {};
      if (targetEl) dependencies[el.id][targetEl.id] = dependency;
    });
  });

  // 3) Propagate constraints from locked elements to ancestors and descendants.
  let lockedElsConflict = false;
  function setLockedElementAncestorConstraints(el, initialLockedEl) {
    const duration = el.getEndTime().diffWithWorkdays(el.getStartTime(), 'minutes', visibleTimeline.workdays);
    el.maxStart = moment(el.maxEnd).addWithWorkdays(-duration, 'minutes', visibleTimeline.workdays);
    if (el.maxStart.isBefore(el.getStartTime())) { // for dragged element
      if (el.getIsLocked()) { lockedElsConflict = true; return; }
      el.setStartTime(el.maxStart);
      el.setEndTime(el.getStartTime().addWithWorkdays(duration, 'minutes', visibleTimeline.workdays));
      el.constrainingLockedElement = initialLockedEl;
      el.changed = true;
    }
    el.predecessors.forEach((predecessor) => {
      const dependency = dependencies[el.id][predecessor.id];
      const { sourceAnchor: currentAnchor, targetAnchor } = getDependencyAnchors(dependency.type, false);
      const maxCurrentAnchor = getAnchorTime(el, currentAnchor);
      const maxTargetAnchor = moment(maxCurrentAnchor).addWithWorkdays(-(dependency.delay || 0), 'days', visibleTimeline.workdays);
      const predecessorDuration = predecessor.getEndTime().diffWithWorkdays(predecessor.getStartTime(), 'minutes', visibleTimeline.workdays);
      if (targetAnchor == 'start') {
        predecessor.maxStart = predecessor.maxStart ? moment.min(predecessor.maxStart, maxTargetAnchor) : maxTargetAnchor;
        const maxEnd = moment(predecessor.maxStart).addWithWorkdays(predecessorDuration, 'minutes', visibleTimeline.workdays);
        predecessor.maxEnd = predecessor.maxEnd ? moment.min(predecessor.maxEnd, maxEnd) : maxEnd;
      } else {
        predecessor.maxEnd = predecessor.maxEnd ? moment.min(predecessor.maxEnd, maxTargetAnchor) : maxTargetAnchor;
        const maxStart = moment(predecessor.maxEnd).addWithWorkdays(-predecessorDuration, 'minutes', visibleTimeline.workdays);
        predecessor.maxStart = predecessor.maxStart ? moment.min(predecessor.maxStart, maxStart) : maxStart;
      }
      setLockedElementAncestorConstraints(predecessor, initialLockedEl || el);
    });
  }

  function setLockedElementDescendantsConstraints(el, initialLockedEl) {
    const duration = el.getEndTime().diffWithWorkdays(el.getStartTime(), 'minutes', visibleTimeline.workdays);
    el.minEnd = moment(el.minStart).addWithWorkdays(duration, 'minutes', visibleTimeline.workdays);
    if (el.minStart.isAfter(el.getStartTime())) { // for dragged element
      if (el.getIsLocked()) { lockedElsConflict = true; return; }
      el.setStartTime(el.minStart);
      el.setEndTime(el.getStartTime().addWithWorkdays(duration, 'minutes', visibleTimeline.workdays));
      el.constrainingLockedElement = initialLockedEl;
      el.changed = true;
    }
    el.successors.forEach((successor) => {
      const dependency = dependencies[el.id][successor.id];
      const { sourceAnchor: currentAnchor, targetAnchor } = getDependencyAnchors(dependency.type, true);
      const minCurrentAnchor = getAnchorTime(el, currentAnchor);
      const minTargetAnchor = moment(minCurrentAnchor).addWithWorkdays(dependency.delay || 0, 'days', visibleTimeline.workdays);
      const successorDuration = successor.getEndTime().diffWithWorkdays(successor.getStartTime(), 'minutes', visibleTimeline.workdays);
      if (targetAnchor == 'start') {
        successor.minStart = successor.minStart ? moment.max(successor.minStart, minTargetAnchor) : minTargetAnchor;
        const minEnd = moment(successor.minStart).addWithWorkdays(successorDuration, 'minutes', visibleTimeline.workdays);
        successor.minEnd = successor.minEnd ? moment.max(successor.minEnd, minEnd) : minEnd;
      } else {
        successor.minEnd = successor.minEnd ? moment.max(successor.minEnd, minTargetAnchor) : minTargetAnchor;
        const minStart = moment(successor.minEnd).addWithWorkdays(-successorDuration, 'minutes', visibleTimeline.workdays);
        successor.minStart = successor.minStart ? moment.max(successor.minStart, minStart) : minStart;
      }
      setLockedElementDescendantsConstraints(successor, initialLockedEl || el);
    });
  }

  relatedElsById.forEach((lockedEl) => {
    if (! lockedEl.getIsLocked()) return;
    lockedEl.maxEnd = lockedEl.getEndTime();
    setLockedElementAncestorConstraints(lockedEl);
    lockedEl.minStart = lockedEl.getStartTime();
    setLockedElementDescendantsConstraints(lockedEl);
  });
  if (lockedElsConflict) {
    const uiPlanningStore = useUiPlanningStore();
    openDependenciesConflictError(relatedElsById);
    uiPlanningStore.setDisplayDependenciesErrors(true);
    return [];
  }

  // 4) Iteration loop: repeatedly propagate updates until no element changes or iteration limit is reached
  function updateElementFromPredecessors(el) {
    if (el.getIsLocked()) return false; // should not be needed but as a security
    let elChanging = false;
    if (el.predecessors.length) {
      const duration = el.getEndTime().diffWithWorkdays(el.getStartTime(), 'minutes', visibleTimeline.workdays);
      if (optimizeDependencies) {
        let minStartLimit = null;
        let optimizedDependencyElement = null;
        el.predecessors.forEach((predecessor) => {
          const dependency = dependencies[el.id][predecessor.id];
          const { sourceAnchor: currentAnchor, targetAnchor } = getDependencyAnchors(dependency.type, false);
          const limitTime = getAnchorTime(predecessor, targetAnchor).addWithWorkdays(dependency.delay || 0, 'days', visibleTimeline.workdays);
          const startLimit = currentAnchor == 'start'
            ? limitTime
            : moment(limitTime).addWithWorkdays(-duration, 'minutes', visibleTimeline.workdays);
          if (! minStartLimit || startLimit.isAfter(minStartLimit)) {
            minStartLimit = startLimit;
            optimizedDependencyElement = predecessor;
          }
        });

        if (minStartLimit) {
          const nextStartTime = clampStartTime(el, minStartLimit, duration, visibleTimeline);
          if (! el.getStartTime().isSame(nextStartTime)) {
            setAnchorTime(el, 'start', nextStartTime, duration, visibleTimeline);
            el.optimizedDependencyElement = optimizedDependencyElement;
            el.changed = true;
            elChanging = true;
          }
        }
        return elChanging;
      }

      el.predecessors.forEach((predecessor) => {
        const dependency = dependencies[el.id][predecessor.id];
        const { sourceAnchor: currentAnchor, targetAnchor } = getDependencyAnchors(dependency.type, false);
        const limitTime = getAnchorTime(predecessor, targetAnchor).addWithWorkdays(dependency.delay || 0, 'days', visibleTimeline.workdays);
        const currentAnchorTime = getAnchorTime(el, currentAnchor);
        const maxAnchor = getMaxAnchorTime(el, currentAnchor, duration, visibleTimeline);
        if (currentAnchorTime.isBefore(limitTime)) {
          if (maxAnchor && ! currentAnchorTime.isBefore(maxAnchor)) return;
          limitTime.add(2 * visibleTimeline.minuteperpx, 'minutes'); // small gap 2 px
          const nextAnchorTime = maxAnchor ? moment.min(maxAnchor, limitTime) : limitTime;
          setAnchorTime(el, currentAnchor, nextAnchorTime, duration, visibleTimeline);
          el.changed = true;
          elChanging = true;
        }
      });
    }
    return elChanging;
  }

  function updateElementFromSuccessors(el) {
    if (el.getIsLocked()) return false; // should not be needed but as a security
    let elChanging = false;
    if (el.successors.length) {
      const duration = el.getEndTime().diffWithWorkdays(el.getStartTime(), 'minutes', visibleTimeline.workdays);
      if (optimizeDependencies) {
        let maxStartLimit = null;
        let optimizedDependencyElement = null;
        el.successors.forEach((successor) => {
          const dependency = dependencies[el.id][successor.id];
          const { sourceAnchor: currentAnchor, targetAnchor } = getDependencyAnchors(dependency.type, true);
          const limitTime = getAnchorTime(successor, targetAnchor).addWithWorkdays(-(dependency.delay || 0), 'days', visibleTimeline.workdays);
          const startLimit = currentAnchor == 'start' ? limitTime : moment(limitTime).addWithWorkdays(-duration, 'minutes', visibleTimeline.workdays);
          if (! maxStartLimit || startLimit.isBefore(maxStartLimit)) {
            maxStartLimit = startLimit;
            optimizedDependencyElement = successor;
          }
        });

        if (maxStartLimit) {
          const nextStartTime = clampStartTime(el, maxStartLimit, duration, visibleTimeline);
          if (! el.getStartTime().isSame(nextStartTime)) {
            setAnchorTime(el, 'start', nextStartTime, duration, visibleTimeline);
            el.optimizedDependencyElement = optimizedDependencyElement;
            el.changed = true;
            elChanging = true;
          }
        }
        return elChanging;
      }

      el.successors.forEach((successor) => {
        const dependency = dependencies[el.id][successor.id];
        const { sourceAnchor: currentAnchor, targetAnchor } = getDependencyAnchors(dependency.type, true);
        const limitTime = getAnchorTime(successor, targetAnchor).addWithWorkdays(-(dependency.delay || 0), 'days', visibleTimeline.workdays);
        const currentAnchorTime = getAnchorTime(el, currentAnchor);
        const minAnchor = getMinAnchorTime(el, currentAnchor, duration, visibleTimeline);
        if (currentAnchorTime.isAfter(limitTime)) {
          if (minAnchor && ! currentAnchorTime.isAfter(minAnchor)) return;
          limitTime.add(-2 * visibleTimeline.minuteperpx, 'minutes'); // small gap 2 px
          const nextAnchorTime = minAnchor ? moment.max(minAnchor, limitTime) : limitTime;
          setAnchorTime(el, currentAnchor, nextAnchorTime, duration, visibleTimeline);
          el.changed = true;
          elChanging = true;
        }
      });
    }
    return elChanging;
  }

  const relatedEls = Array.from(relatedElsById.values());

  if (optimizeDependencies) {
    const initialTimesById = relatedEls.reduce((acc, el) => {
      acc.set(el.id, { start: el.getStartTime(), end: el.getEndTime() });
      return acc;
    }, new Map());

    const getStateSignature = () => relatedEls.map(el => `${el.id}:${el.getStartTime().valueOf()}:${el.getEndTime().valueOf()}`).sort().join('|');

    const entryEls = reverse ? relatedEls.filter(el => ! el.successors?.length) : relatedEls.filter(el => ! el.predecessors?.length);
    const scanEls = entryEls.length ? entryEls : relatedEls;

    let changing = true;
    let iterationsLimit = 100;
    while (changing && iterationsLimit) {
      const previousState = getStateSignature();
      iterationsLimit--;
      const reducedElements = new Set();

      for (let i = 0; i < scanEls.length; i++) {
        const el = scanEls[i];
        if (reverse) {
          calculateElPredecessors(reducedElements, relatedEls, el, 100, false);
        } else {
          calculateElSuccessors(reducedElements, relatedEls, el, 100, false);
        }
      }

      relatedEls.forEach((el) => {
        if (el.getIsLocked()) return;
        const duration = el.getEndTime().diffWithWorkdays(el.getStartTime(), 'minutes', visibleTimeline.workdays);
        const clampedStart = clampStartTime(el, el.getStartTime(), duration, visibleTimeline);
        if (! el.getStartTime().isSame(clampedStart)) {
          setAnchorTime(el, 'start', clampedStart, duration, visibleTimeline);
        }
      });

      changing = previousState !== getStateSignature();
    }

    const uiPlanningStore = useUiPlanningStore();
    if (iterationsLimit === 0) {
      openDependenciesConflictError(relatedElsById);
      uiPlanningStore.setDisplayDependenciesErrors(true);
      return [];
    }

    relatedEls.forEach((el) => {
      const initialTimes = initialTimesById.get(el.id);
      if (! initialTimes) return;
      if (! el.getStartTime().isSame(initialTimes.start) || ! el.getEndTime().isSame(initialTimes.end)) {
        el.changed = true;
      }
    });

    uiPlanningStore.setDisplayDependenciesErrors(false);
    return relatedEls.filter(item => item.changed);
  }

  let changing = true;
  let iterationsLimit = 100;
  while (changing && iterationsLimit) {
    changing = false;
    iterationsLimit--;
    for (let i = 0; i < relatedEls.length; i++) {
      const el = relatedEls[i];
      const elChanging = reverse ? updateElementFromSuccessors(el) : updateElementFromPredecessors(el);
      changing = changing || elChanging;
    }
  }
  const uiPlanningStore = useUiPlanningStore();
  if (iterationsLimit === 0) {
    openDependenciesConflictError(relatedElsById);
    uiPlanningStore.setDisplayDependenciesErrors(true);
    return [];
  }
  uiPlanningStore.setDisplayDependenciesErrors(false);
  return relatedEls.filter(item => item.changed);
}

function getElEndLimitFromSuccessor(sourceEl, successorEl, dependency, visibleTimeline) {
  const delay = dependency.delay || 0;
  const dependencyType = dependency.type;
  const sourceDuration = sourceEl.getEndTime().diffWithWorkdays(sourceEl.getStartTime(), 'minutes', visibleTimeline.workdays);
  if (dependencyType == 'SS') {
    return successorEl.getStartTime().addWithWorkdays(-delay, 'days', visibleTimeline.workdays).addWithWorkdays(sourceDuration, 'minutes', visibleTimeline.workdays);
  }
  if (dependencyType == 'FF') {
    return successorEl.getEndTime().addWithWorkdays(-delay, 'days', visibleTimeline.workdays);
  }
  return successorEl.getStartTime().addWithWorkdays(-delay, 'days', visibleTimeline.workdays); // FS
}

function getElStartLimitFromPredecessor(sourceEl, predecessorEl, dependency, visibleTimeline) {
  const delay = dependency.delay || 0;
  const dependencyType = dependency.type;
  const sourceDuration = sourceEl.getEndTime().diffWithWorkdays(sourceEl.getStartTime(), 'minutes', visibleTimeline.workdays);
  if (dependencyType == 'SS') {
    return predecessorEl.getStartTime().addWithWorkdays(delay, 'days', visibleTimeline.workdays);
  }
  if (dependencyType == 'FF') {
    return predecessorEl.getEndTime().addWithWorkdays(delay, 'days', visibleTimeline.workdays).addWithWorkdays(-sourceDuration, 'minutes', visibleTimeline.workdays);
  }
  return predecessorEl.getEndTime().addWithWorkdays(delay, 'days', visibleTimeline.workdays); // FS
}

function calculateElPredecessors(reducedElements /* Set */, relatedEls, relatedEl, depthLimit = 100, addGap = true) {
  // relatedEls: elements.filter(item => item.getDependencies()?.length && item.hasDates()).map(el => angular.copy(el));
  if (depthLimit <= 0) { // infinite loop
    reducedElements.clear();
    return reducedElements;
  }

  const dependencies = relatedEl.getDependencies();
  const successors = dependencies.filter(dependency => dependency.successor);
  const predecessors = dependencies.filter(dependency => ! dependency.successor);
  if (successors.length) {
    const { visibleTimeline } = relatedEl.getPlanning();
    let minEndtime = null;
    let optimizedDependencyElement = null;
    successors.forEach((successorDependency) => {
      const successorEl = relatedEls.find(item => item.id == successorDependency.target_id);
      if (! successorEl) return;
      const limit = getElEndLimitFromSuccessor(relatedEl, successorEl, successorDependency, visibleTimeline);
      if (! minEndtime || limit.isBefore(minEndtime)) {
        minEndtime = limit;
        optimizedDependencyElement = successorEl;
      }
    });
    if (minEndtime) {
      if (addGap) minEndtime.add(2 * -visibleTimeline.minuteperpx, 'minutes'); // small gap 2 px
      if (! relatedEl.getIsLocked() && relatedEl.getEndTime().format() != minEndtime.format()) { // If no critical link with predecessor
        const duration = relatedEl.getEndTime().diffWithWorkdays(relatedEl.getStartTime(), 'minutes', visibleTimeline.workdays);
        const newStartTime = minEndtime.clone().addWithWorkdays(-duration, 'minutes', visibleTimeline.workdays);
        relatedEl.setStartTime(newStartTime);
        relatedEl.setEndTime(minEndtime);
        relatedEl.optimizedDependencyElement = optimizedDependencyElement;
        reducedElements.add(relatedEl);
      }
    }
  }
  if (! predecessors.length) return reducedElements;
  const predecessorsEls = predecessors.map(predecessor => relatedEls.find(item => item.id == predecessor.target_id));
  predecessorsEls.forEach((predecessorEl) => {
    if (predecessorEl) calculateElPredecessors(reducedElements, relatedEls, predecessorEl, depthLimit - 1, addGap);
  });
  return reducedElements;
}

function calculateElSuccessors(reducedElements /* Set */, relatedEls, relatedEl, depthLimit = 100, addGap = true) {
  // relatedEls: elements.filter(item => item.getDependencies()?.length && item.hasDates()).map(el => angular.copy(el));
  if (depthLimit <= 0) { // infinite loop
    reducedElements.clear();
    return reducedElements;
  }

  const dependencies = relatedEl.getDependencies();
  const successors = dependencies.filter(dependency => dependency.successor);
  const predecessors = dependencies.filter(dependency => ! dependency.successor);
  if (predecessors.length) {
    const { visibleTimeline } = relatedEl.getPlanning();
    let minStarttime = null;
    let optimizedDependencyElement = null;
    predecessors.forEach((predecessorDependency) => {
      const predecessorEl = relatedEls.find(item => item.id == predecessorDependency.target_id);
      if (! predecessorEl) return;
      const limit = getElStartLimitFromPredecessor(relatedEl, predecessorEl, predecessorDependency, visibleTimeline);
      if (! minStarttime || limit.isAfter(minStarttime)) {
        minStarttime = limit;
        optimizedDependencyElement = predecessorEl;
      }
    });
    if (minStarttime) {
      if (addGap) minStarttime.add(2 * visibleTimeline.minuteperpx, 'minutes'); // small gap 2 px
      if (! relatedEl.getIsLocked() && relatedEl.getStartTime().format() != minStarttime.format()) { // If no critical link with predecessor
        const duration = relatedEl.getEndTime().diffWithWorkdays(relatedEl.getStartTime(), 'minutes', visibleTimeline.workdays);
        const newEndTime = minStarttime.clone().addWithWorkdays(duration, 'minutes', visibleTimeline.workdays);
        relatedEl.setStartTime(minStarttime);
        relatedEl.setEndTime(newEndTime);
        relatedEl.optimizedDependencyElement = optimizedDependencyElement || null;
        reducedElements.add(relatedEl);
      }
    }
  }
  if (! successors.length) return reducedElements;
  const successorsEls = successors.map(successor => relatedEls.find(item => item.id == successor.target_id));
  successorsEls.forEach((successorEl) => {
    if (successorEl) calculateElSuccessors(reducedElements, relatedEls, successorEl, depthLimit - 1, addGap);
  });
  return reducedElements;
}

export default {
  calculate,
  calculateElPredecessors,
  calculateElSuccessors,
};
